import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
const NCRTrackerDashboard: React.FC = () => {
  const kpis = [{
    title: 'Open NCRs',
    value: '215',
    change: '-18',
    isPositive: true,
    subtitle: 'Active non-conformances'
  }, {
    title: 'Critical NCRs',
    value: '36',
    change: '-5',
    isPositive: true,
    subtitle: 'High priority'
  }, {
    title: 'Avg NCR Age (days)',
    value: '12.4',
    change: '-2.1',
    isPositive: true,
    subtitle: 'Average resolution time'
  }];
  // NCR Ageing Chart Data
  const ncrAgeingData = [{
    ageRange: '0-7 days',
    count: 85,
    percentage: 39.5
  }, {
    ageRange: '8-14 days',
    count: 62,
    percentage: 28.8
  }, {
    ageRange: '15-30 days',
    count: 42,
    percentage: 19.5
  }, {
    ageRange: '31-60 days',
    count: 18,
    percentage: 8.4
  }, {
    ageRange: '>60 days',
    count: 8,
    percentage: 3.7
  }];
  // Severity Distribution Data
  const severityDistributionData = [{
    name: 'Critical',
    value: 36,
    percentage: 16.7
  }, {
    name: 'Major',
    value: 77,
    percentage: 35.8
  }, {
    name: 'Minor',
    value: 102,
    percentage: 47.4
  }];
  const SEVERITY_COLORS = {
    Critical: '#ef4444',
    Major: '#f59e0b',
    Minor: '#fbbf24'
  };
  // NCR Trend Data
  const ncrTrendData = [{
    month: 'Jan',
    opened: 42,
    closed: 35,
    open: 233
  }, {
    month: 'Feb',
    opened: 38,
    closed: 40,
    open: 231
  }, {
    month: 'Mar',
    opened: 45,
    closed: 42,
    open: 234
  }, {
    month: 'Apr',
    opened: 40,
    closed: 44,
    open: 230
  }, {
    month: 'May',
    opened: 36,
    closed: 41,
    open: 225
  }, {
    month: 'Jun',
    opened: 34,
    closed: 44,
    open: 215
  }];
  // NCR Listing Data
  const ncrListingData = [{
    ncrId: 'NCR-2024-001',
    date: '2024-01-15',
    plant: 'Manesar',
    area: 'Production Floor',
    category: 'Process Control',
    description: 'Torque specification not followed on Line 3 assembly',
    severity: 'Critical',
    raisedBy: 'Quality Inspector A',
    assignedTo: 'Production Supervisor',
    status: 'In Progress',
    dueDate: '2024-02-15',
    age: 28,
    rootCause: 'Operator training gap',
    correctiveAction: 'Retraining scheduled for all operators',
    preventiveAction: 'Updated work instruction with visual aids'
  }, {
    ncrId: 'NCR-2024-002',
    date: '2024-01-18',
    plant: 'Chennai',
    area: 'Warehouse',
    category: 'Material Handling',
    description: 'Damaged components received from supplier',
    severity: 'Major',
    raisedBy: 'Warehouse Manager',
    assignedTo: 'Supply Chain Team',
    status: 'Open',
    dueDate: '2024-02-18',
    age: 25,
    rootCause: 'Inadequate packaging by supplier',
    correctiveAction: 'Supplier notified, replacement requested',
    preventiveAction: 'Packaging standards communicated to supplier'
  }, {
    ncrId: 'NCR-2024-003',
    date: '2024-01-20',
    plant: 'Pune',
    area: 'Quality Lab',
    category: 'Measurement System',
    description: 'CMM calibration certificate expired',
    severity: 'Major',
    raisedBy: 'Lab Manager',
    assignedTo: 'Maintenance Team',
    status: 'In Progress',
    dueDate: '2024-02-05',
    age: 23,
    rootCause: 'Calibration schedule not monitored',
    correctiveAction: 'External calibration scheduled',
    preventiveAction: 'Automated calibration reminder system implemented'
  }, {
    ncrId: 'NCR-2024-004',
    date: '2024-01-22',
    plant: 'Bangalore',
    area: 'Line 2',
    category: 'Quality Control',
    description: 'Parts found with dimensional deviation beyond tolerance',
    severity: 'Critical',
    raisedBy: 'QC Inspector B',
    assignedTo: 'Line Supervisor',
    status: 'In Progress',
    dueDate: '2024-01-29',
    age: 21,
    rootCause: 'Tool wear not detected',
    correctiveAction: 'Tool replaced, parts reworked',
    preventiveAction: 'Tool life monitoring system enhanced'
  }, {
    ncrId: 'NCR-2024-005',
    date: '2024-01-25',
    plant: 'Manesar',
    area: 'Chemical Storage',
    category: 'Safety',
    description: 'Incompatible chemicals stored together',
    severity: 'Critical',
    raisedBy: 'Safety Officer',
    assignedTo: 'Warehouse Supervisor',
    status: 'Closed',
    dueDate: '2024-02-01',
    age: 18,
    rootCause: 'Storage procedure not followed',
    correctiveAction: 'Chemical segregation implemented',
    preventiveAction: 'Storage area reorganized with clear labeling'
  }, {
    ncrId: 'NCR-2024-006',
    date: '2024-01-28',
    plant: 'Chennai',
    area: 'Line 1',
    category: 'Process Control',
    description: 'First piece inspection not documented',
    severity: 'Minor',
    raisedBy: 'Quality Team A',
    assignedTo: 'QC Inspector',
    status: 'Open',
    dueDate: '2024-02-28',
    age: 15,
    rootCause: 'Inspection form not available at workstation',
    correctiveAction: 'Inspection forms provided',
    preventiveAction: 'Digital inspection system being implemented'
  }, {
    ncrId: 'NCR-2024-007',
    date: '2024-02-01',
    plant: 'Pune',
    area: 'Maintenance Shop',
    category: 'Workplace Organization',
    description: 'Oil spills not cleaned immediately',
    severity: 'Major',
    raisedBy: 'Quality Team B',
    assignedTo: 'Maintenance Head',
    status: 'In Progress',
    dueDate: '2024-02-22',
    age: 12,
    rootCause: 'Cleaning schedule not followed',
    correctiveAction: 'Daily cleaning checklist implemented',
    preventiveAction: 'Spill containment kits placed at strategic locations'
  }, {
    ncrId: 'NCR-2024-008',
    date: '2024-02-03',
    plant: 'Bangalore',
    area: 'Production Floor',
    category: 'Safety',
    description: 'Machine guard missing on CNC-5',
    severity: 'Critical',
    raisedBy: 'Safety Team',
    assignedTo: 'Maintenance Head',
    status: 'In Progress',
    dueDate: '2024-02-06',
    age: 10,
    rootCause: 'Guard removed for maintenance, not reinstalled',
    correctiveAction: 'Guard fabrication in progress',
    preventiveAction: 'Maintenance checklist updated to include guard verification'
  }, {
    ncrId: 'NCR-2024-009',
    date: '2024-02-05',
    plant: 'Manesar',
    area: 'Warehouse',
    category: 'Documentation',
    description: 'Inventory labels faded and illegible',
    severity: 'Minor',
    raisedBy: 'Quality Team A',
    assignedTo: 'Warehouse Supervisor',
    status: 'Open',
    dueDate: '2024-02-25',
    age: 8,
    rootCause: 'Label quality not suitable for warehouse environment',
    correctiveAction: 'New labeling system being procured',
    preventiveAction: 'Durable label material specified'
  }, {
    ncrId: 'NCR-2024-010',
    date: '2024-02-08',
    plant: 'Chennai',
    area: 'Loading Bay',
    category: 'Safety',
    description: 'PPE not worn by forklift operators',
    severity: 'Major',
    raisedBy: 'Safety Team',
    assignedTo: 'Logistics Manager',
    status: 'In Progress',
    dueDate: '2024-02-15',
    age: 5,
    rootCause: 'PPE compliance not monitored',
    correctiveAction: 'Disciplinary action taken, retraining ongoing',
    preventiveAction: 'Daily PPE compliance checks implemented'
  }, {
    ncrId: 'NCR-2024-011',
    date: '2024-02-10',
    plant: 'Pune',
    area: 'Line 3',
    category: 'Traceability',
    description: 'Material traceability not maintained',
    severity: 'Major',
    raisedBy: 'Quality Team B',
    assignedTo: 'Production Supervisor',
    status: 'Open',
    dueDate: '2024-02-28',
    age: 3,
    rootCause: 'Barcode scanning system not used consistently',
    correctiveAction: 'Barcode scanning made mandatory',
    preventiveAction: 'System audit scheduled weekly'
  }, {
    ncrId: 'NCR-2024-012',
    date: '2024-02-12',
    plant: 'Bangalore',
    area: 'Quality Lab',
    category: 'Quality Control',
    description: 'Test results not recorded in system',
    severity: 'Minor',
    raisedBy: 'Lab Supervisor',
    assignedTo: 'Lab Technician',
    status: 'Open',
    dueDate: '2024-02-26',
    age: 1,
    rootCause: 'Manual recording process prone to omission',
    correctiveAction: 'Backlog data entry completed',
    preventiveAction: 'Automated data capture system being evaluated'
  }];
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return 'bg-red-100 text-red-700';
      case 'Major':
        return 'bg-orange-100 text-orange-700';
      case 'Minor':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Closed':
        return 'bg-green-100 text-green-700';
      case 'In Progress':
        return 'bg-blue-100 text-blue-700';
      case 'Open':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getAgeColor = (age: number) => {
    if (age <= 7) return 'text-green-600';
    if (age <= 14) return 'text-blue-600';
    if (age <= 30) return 'text-orange-600';
    return 'text-red-600';
  };
  return <div data-id="element-2293">
      <DescriptionPanel title="NCR (Non-Conformance Report) Tracker" description="Comprehensive tracking system for all non-conformance reports across plants. Monitor NCR status, severity distribution, ageing, and closure trends to drive continuous improvement and maintain quality standards." insights={['Total open NCRs reduced to 215 from 233 last month - 7.7% improvement', 'Critical NCRs down to 36 - focused attention reducing high-severity issues', 'Average NCR age improved to 12.4 days - faster resolution process', '39.5% of NCRs closed within 7 days - exceeding target of 35%']} data-id="element-2294" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-2295">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-2296">
          NCR Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2297">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-2298">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-2299">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-2300">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-2301">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-2302">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-2303" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-2304" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* NCR Ageing Chart and Severity Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-2305">
        {/* NCR Ageing Chart */}
        <Card className="p-6" data-id="element-2306">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2307">
            NCR Ageing Chart
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-2308">
            Distribution of open NCRs by age range
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-2309">
            <BarChart data={ncrAgeingData} data-id="element-2310">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-2311" />
              <XAxis dataKey="ageRange" tick={{
              fontSize: 11
            }} angle={-15} textAnchor="end" height={80} data-id="element-2312" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-2313" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: any, name: string) => {
              if (name === 'count') {
                const item = ncrAgeingData.find(d => d.count === value);
                return [`${value} NCRs (${item?.percentage}%)`, 'Count'];
              }
              return [value, name];
            }} data-id="element-2314" />
              <Legend data-id="element-2315" />
              <Bar dataKey="count" name="NCR Count" data-id="element-2316">
                {ncrAgeingData.map((entry, index) => <Cell key={`cell-${index}`} fill={index === 0 ? '#10b981' : index === 1 ? '#3b82f6' : index === 2 ? '#f59e0b' : index === 3 ? '#f97316' : '#ef4444'} data-id="element-2317" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2318">
            <div className="grid grid-cols-5 gap-2 text-xs" data-id="element-2319">
              <div className="text-center" data-id="element-2320">
                <div className="text-gray-600" data-id="element-2321">0-7 days</div>
                <div className="text-sm font-bold text-green-600" data-id="element-2322">85</div>
                <div className="text-xs text-gray-500" data-id="element-2323">39.5%</div>
              </div>
              <div className="text-center" data-id="element-2324">
                <div className="text-gray-600" data-id="element-2325">8-14 days</div>
                <div className="text-sm font-bold text-blue-600" data-id="element-2326">62</div>
                <div className="text-xs text-gray-500" data-id="element-2327">28.8%</div>
              </div>
              <div className="text-center" data-id="element-2328">
                <div className="text-gray-600" data-id="element-2329">15-30 days</div>
                <div className="text-sm font-bold text-orange-600" data-id="element-2330">42</div>
                <div className="text-xs text-gray-500" data-id="element-2331">19.5%</div>
              </div>
              <div className="text-center" data-id="element-2332">
                <div className="text-gray-600" data-id="element-2333">31-60 days</div>
                <div className="text-sm font-bold text-orange-700" data-id="element-2334">18</div>
                <div className="text-xs text-gray-500" data-id="element-2335">8.4%</div>
              </div>
              <div className="text-center" data-id="element-2336">
                <div className="text-gray-600" data-id="element-2337">&gt;60 days</div>
                <div className="text-sm font-bold text-red-600" data-id="element-2338">8</div>
                <div className="text-xs text-gray-500" data-id="element-2339">3.7%</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Severity Distribution */}
        <Card className="p-6" data-id="element-2340">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2341">
            Severity Distribution
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-2342">
            NCR breakdown by severity level
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-2343">
            <PieChart data-id="element-2344">
              <Pie data={severityDistributionData} cx="50%" cy="50%" labelLine={false} label={({
              name,
              percentage
            }) => `${name}: ${percentage}%`} outerRadius={120} fill="#8884d8" dataKey="value" data-id="element-2345">
                {severityDistributionData.map((entry, index) => <Cell key={`cell-${index}`} fill={SEVERITY_COLORS[entry.name as keyof typeof SEVERITY_COLORS]} data-id="element-2346" />)}
              </Pie>
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-2347" />
              <Legend data-id="element-2348" />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2349">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-2350">
              <div className="text-center" data-id="element-2351">
                <div className="text-gray-600" data-id="element-2352">Critical</div>
                <div className="text-xl font-bold text-red-600" data-id="element-2353">36</div>
                <div className="text-xs text-gray-500" data-id="element-2354">16.7%</div>
              </div>
              <div className="text-center" data-id="element-2355">
                <div className="text-gray-600" data-id="element-2356">Major</div>
                <div className="text-xl font-bold text-orange-600" data-id="element-2357">77</div>
                <div className="text-xs text-gray-500" data-id="element-2358">35.8%</div>
              </div>
              <div className="text-center" data-id="element-2359">
                <div className="text-gray-600" data-id="element-2360">Minor</div>
                <div className="text-xl font-bold text-yellow-600" data-id="element-2361">102</div>
                <div className="text-xs text-gray-500" data-id="element-2362">47.4%</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* NCR Trend */}
      <Card className="p-6 mb-8" data-id="element-2363">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2364">NCR Trend</h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-2365">
          Monthly trend of NCRs opened, closed, and total open count
        </p>
        <ResponsiveContainer width="100%" height={350} data-id="element-2366">
          <LineChart data={ncrTrendData} data-id="element-2367">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-2368" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-2369" />
            <YAxis yAxisId="left" tick={{
            fontSize: 12
          }} data-id="element-2370" />
            <YAxis yAxisId="right" orientation="right" tick={{
            fontSize: 12
          }} data-id="element-2371" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-2372" />
            <Legend data-id="element-2373" />
            <Line yAxisId="left" type="monotone" dataKey="opened" stroke="#ef4444" strokeWidth={3} name="Opened" dot={{
            r: 5
          }} data-id="element-2374" />
            <Line yAxisId="left" type="monotone" dataKey="closed" stroke="#10b981" strokeWidth={3} name="Closed" dot={{
            r: 5
          }} data-id="element-2375" />
            <Line yAxisId="right" type="monotone" dataKey="open" stroke="#3b82f6" strokeWidth={3} name="Total Open" dot={{
            r: 5
          }} data-id="element-2376" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2377">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-2378">
            <div className="text-center" data-id="element-2379">
              <div className="text-gray-600" data-id="element-2380">Current Month</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-2381">Jun 2024</div>
            </div>
            <div className="text-center" data-id="element-2382">
              <div className="text-gray-600" data-id="element-2383">Opened</div>
              <div className="text-xl font-bold text-red-600" data-id="element-2384">34</div>
              <div className="text-xs text-gray-500" data-id="element-2385">this month</div>
            </div>
            <div className="text-center" data-id="element-2386">
              <div className="text-gray-600" data-id="element-2387">Closed</div>
              <div className="text-xl font-bold text-green-600" data-id="element-2388">44</div>
              <div className="text-xs text-gray-500" data-id="element-2389">this month</div>
            </div>
            <div className="text-center" data-id="element-2390">
              <div className="text-gray-600" data-id="element-2391">Total Open</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-2392">215</div>
              <div className="text-xs text-gray-500" data-id="element-2393">current</div>
            </div>
          </div>
        </div>
      </Card>
      {/* NCR Listing Table */}
      <Card className="p-6" data-id="element-2394">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-2395">
          NCR Listing
        </h3>
        <div className="overflow-x-auto" data-id="element-2396">
          <table className="w-full" data-id="element-2397">
            <thead data-id="element-2398">
              <tr className="border-b-2 border-gray-300" data-id="element-2399">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2400">
                  NCR ID
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2401">
                  Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2402">
                  Plant
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2403">
                  Area
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2404">
                  Category
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2405">
                  Description
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2406">
                  Severity
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2407">
                  Raised By
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2408">
                  Assigned To
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2409">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2410">
                  Due Date
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2411">
                  Age (days)
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2412">
                  Root Cause
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2413">
                  Corrective Action
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2414">
                  Preventive Action
                </th>
              </tr>
            </thead>
            <tbody data-id="element-2415">
              {ncrListingData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-2416">
                  <td className="py-3 px-4 text-sm font-medium text-blue-600" data-id="element-2417">
                    {row.ncrId}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2418">
                    {row.date}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-2419">
                    {row.plant}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2420">
                    {row.area}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2421">
                    {row.category}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-2422">
                    {row.description}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2423">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getSeverityColor(row.severity)}`} data-id="element-2424">
                      {row.severity}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2425">
                    {row.raisedBy}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2426">
                    {row.assignedTo}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2427">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(row.status)}`} data-id="element-2428">
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2429">
                    {row.dueDate}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2430">
                    <span className={`text-sm font-bold ${getAgeColor(row.age)}`} data-id="element-2431">
                      {row.age}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-2432">
                    {row.rootCause}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-2433">
                    {row.correctiveAction}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-2434">
                    {row.preventiveAction}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-2435">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-2436">
            <div className="text-center" data-id="element-2437">
              <div className="text-gray-600" data-id="element-2438">Total NCRs</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-2439">12</div>
              <div className="text-xs text-gray-500" data-id="element-2440">displayed</div>
            </div>
            <div className="text-center" data-id="element-2441">
              <div className="text-gray-600" data-id="element-2442">Critical</div>
              <div className="text-xl font-bold text-red-600" data-id="element-2443">4</div>
              <div className="text-xs text-gray-500" data-id="element-2444">high priority</div>
            </div>
            <div className="text-center" data-id="element-2445">
              <div className="text-gray-600" data-id="element-2446">Open</div>
              <div className="text-xl font-bold text-yellow-600" data-id="element-2447">6</div>
              <div className="text-xs text-gray-500" data-id="element-2448">pending</div>
            </div>
            <div className="text-center" data-id="element-2449">
              <div className="text-gray-600" data-id="element-2450">In Progress</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-2451">5</div>
              <div className="text-xs text-gray-500" data-id="element-2452">ongoing</div>
            </div>
            <div className="text-center" data-id="element-2453">
              <div className="text-gray-600" data-id="element-2454">Closed</div>
              <div className="text-xl font-bold text-green-600" data-id="element-2455">1</div>
              <div className="text-xs text-gray-500" data-id="element-2456">completed</div>
            </div>
            <div className="text-center" data-id="element-2457">
              <div className="text-gray-600" data-id="element-2458">Avg Age</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-2459">12.4</div>
              <div className="text-xs text-gray-500" data-id="element-2460">days</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default NCRTrackerDashboard;